import moment from 'moment';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetFullEmploymentData } from '~/data/employment/useFullEmploymentData';
import {
  ProposalProps,
  ProposalStatus,
  SalaryProposalProps,
} from '~/data/proposals';
import { useProposalUtils } from '~/data/proposals/useProposalUtils';
import { useSelectedEmployeeId } from '~/hooks/employee/useSelectedEmployeeId';
import { CurrentEmploymentSalary } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/SalaryProposalForm/data';
import {
  ProposalFormPropsMessage,
  ProposalFormPropsMessageContext,
} from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposals/components/Common';
import { EmployeeProposalsLayoutProps } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposals/components/EmployeeProposalsLayout/component';
import { filterTruthy, getLastInArray, sortDates } from '~/utils/helper';

export type SalaryProposalFieldProps = {
  datePickers: {
    startDate: {
      minDate?: Date;
    };
  };
};

export type SalaryProposalFormProps = {
  currentEmploymentSalary: CurrentEmploymentSalary;
  newProposal: SalaryProposalFieldProps;
  pendingProposal: SalaryProposalFieldProps;
  messages: ProposalFormPropsMessage[];
  disabledDeleteProposals: (string | undefined)[];
};

type UseSalaryProposalsLayoutOutput = {
  newProposal?: SalaryProposalProps;
  formProps: SalaryProposalFormProps;
  isLoading: boolean;
};

export const useSalaryProposalsLayout = (
  props: EmployeeProposalsLayoutProps<SalaryProposalProps>,
): UseSalaryProposalsLayoutOutput => {
  const { proposals, newProposal } = props;
  const { t } = useTranslation();

  const { selectedEmployeeId } = useSelectedEmployeeId();
  const employeeId = newProposal?.targetId || selectedEmployeeId;

  // Current salary info
  const { data: fullEmployment, isLoading: isLoadingFullEmployementData } =
    useGetFullEmploymentData({
      employeeId,
    });

  const currentSalary = useMemo(
    () => fullEmployment?.emplSalaries[0],
    [fullEmployment],
  );

  const emplBasicStartDate = useMemo(
    () => fullEmployment?.emplBasic?.fEmplDateStart,
    [fullEmployment],
  );

  const { currentEmploymentSalary, currentStartDate } = useMemo(() => {
    //Current start date will become today if currentSalary?.fSalaryStartDate is undefined
    const currentStartDate = currentSalary?.fSalaryStartDate
      ? moment(currentSalary?.fSalaryStartDate).toDate()
      : moment(emplBasicStartDate).toDate();

    const currentEmploymentSalary: CurrentEmploymentSalary = {
      employmentId: currentSalary?.fEmplId || undefined,
      salaryFrequency: currentSalary?.fSalaryFrequency || '',
      baseSalary: currentSalary?.fSalaryBaseSalary || 0,
    };

    return {
      currentEmploymentSalary,
      currentStartDate,
    };
  }, [currentSalary, emplBasicStartDate]);

  // Proposals salary info
  const { filterProposalsByStatus } = useProposalUtils();

  const { newProposalEnabled, ...formFieldProps } = useMemo(() => {
    const isProposalEndDateUndefined = (proposals: ProposalProps[]): boolean =>
      !!proposals.find((p) => !p.proposalData?.endDate);

    const getLatestDate = (dates: Date[]) =>
      getLastInArray(sortDates(filterTruthy(dates)));

    // Pending end date check
    const pendingProposals = filterProposalsByStatus(
      [ProposalStatus.IN_PROGRESS],
      proposals,
    );
    const checkPendingProposalsEndDate = !!pendingProposals.length
      ? !isProposalEndDateUndefined(pendingProposals)
      : true;

    // StartDate min date
    const upcomingProposals = filterProposalsByStatus(
      [ProposalStatus.COMPLETED],
      proposals,
    ).filter((p) =>
      moment(p.proposalData?.startDate).isSameOrAfter(currentStartDate),
    );

    const temp = [...upcomingProposals];
    const orederedProposalsByDate = temp.sort((a, b) =>
      moment(a.createdTime).diff(moment(b.createdTime)),
    );

    const disabledDeleteProposals =
      orederedProposalsByDate.length < 1
        ? []
        : orederedProposalsByDate.map((prop) => prop.proposalId).slice(0, -1);

    const getProposalsMinStartDate = (
      proposals: SalaryProposalProps[],
    ): Date | undefined => {
      const proposalsStartDates = proposals
        .filter((p) => !!p.proposalData?.startDate)
        .map((p) => moment(p.proposalData?.startDate).toDate());
      const proposalsEndDates = proposals
        .filter((p) => !!p.proposalData?.endDate)
        .map((p) => moment(p.proposalData?.endDate).add('1', 'days').toDate());

      return getLatestDate([
        currentStartDate,
        ...proposalsStartDates,
        ...proposalsEndDates,
      ]);
    };

    const getFieldProps = (
      proposals: SalaryProposalProps[],
    ): SalaryProposalFieldProps => {
      return {
        datePickers: {
          startDate: {
            minDate: getProposalsMinStartDate(proposals),
          },
        },
      };
    };

    const messages: ProposalFormPropsMessage[] = [
      {
        show: !checkPendingProposalsEndDate,
        payload: {
          context: ProposalFormPropsMessageContext.NewProposal,
          message: `${t('createNewProposalUnavailable')}: ${t(
            'proposalWithoutEndDateIsNotClosed',
          )}`,
        },
      },
    ]
      .filter((m) => m.show)
      .map((m) => m.payload);

    return {
      newProposalEnabled: checkPendingProposalsEndDate,
      newProposal: getFieldProps([...upcomingProposals, ...pendingProposals]),
      pendingProposal: getFieldProps(upcomingProposals),
      messages,
      disabledDeleteProposals,
    };
  }, [filterProposalsByStatus, proposals, currentStartDate, t]);

  const formProps: SalaryProposalFormProps = {
    currentEmploymentSalary,
    ...formFieldProps,
  };

  return {
    newProposal: newProposalEnabled ? newProposal : undefined,
    formProps,
    isLoading: isLoadingFullEmployementData,
  };
};
